/**
 * The Incidents layer on the project map: every incident of the project —
 * open, silent rekindle, stood down, quiet AND closed history — drawn as
 * its detection-footprint hull plus a numbered marker, coloured by status,
 * with dashed links from a closed incident to the rekindle that reopened
 * it. Independent of the realtime detection layers (which can be off) and
 * on by default, so "there is an incident here" is visible the moment the
 * map opens.
 *
 * Clicking a marker or hull opens IncidentPopover; "More" hands the id to
 * the host (ProjectPage), which opens the detail modal.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import type { FeatureCollection } from "geojson";
import type * as mapboxgl from "mapbox-gl";

import ToolbarButton from "../toolbar/ToolbarButton";
import { useMap } from "../../contexts/MapContext";
import {
  IncidentStatus,
  IncidentSummary,
  useProjectIncidents,
} from "../../api/incidentLog";
import { useProjectAgentSessions } from "../../api/agentSessions";
import {
  incidentHullFeatures,
  incidentLineageBounds,
  incidentLinkFeatures,
  incidentMarkerFeatures,
} from "../../utils/incidentFeatures";
import {
  INCIDENT_STATUS_GROUPS,
  INCIDENT_STATUS_META,
} from "../../utils/incidentRekindle";
import IncidentPopover from "../incidents/IncidentPopover";
import CloseIncidentDialog from "../incidents/CloseIncidentDialog";
import { mapEventScreenPoint } from "../../utils/uiZoom";
import {
  incidentHullFillStyle,
  incidentHullLineStyle,
  incidentLinkStyle,
  incidentMarkerLabelStyle,
  incidentMarkerStyle,
} from "./incidentStyles";
import {
  ALL_LEGEND_SLOTS,
  LEGEND_PRIORITY,
  LEGEND_SLOT_STYLES,
  LEGEND_WIDTH,
  useLegendSlot,
} from "./legendSlots";

const EMPTY_FC: FeatureCollection = { type: "FeatureCollection", features: [] };

export type IncidentGroupKey = (typeof INCIDENT_STATUS_GROUPS)[number]["key"];
export type IncidentGroupVisibility = Record<IncidentGroupKey, boolean>;

export const DEFAULT_INCIDENT_GROUPS: IncidentGroupVisibility = {
  attention: true,
  watching: true,
  closed: true,
};

export const visibleStatuses = (groups: IncidentGroupVisibility): Set<IncidentStatus> =>
  new Set(
    INCIDENT_STATUS_GROUPS.filter((g) => groups[g.key]).flatMap((g) => g.statuses)
  );

interface IncidentsLayerProps {
  id: string;
  name: string;
  projectId: number | undefined;
  /** Closed history window in days (0 = everything). */
  days: number;
  groups: IncidentGroupVisibility;
  selectedIncidentId: number | null;
  onSelect: (incidentId: number | null) => void;
  /** Bump to zoom the map to the selected incident and its lineage. */
  focusSignal: number;
  onOpenDetail: (incidentId: number) => void;
  /** False when the organisation is not entitled to any realtime detection
   *  kind. Every incident is raised from a detection, so such a tenant can
   *  never have one: the layer is not offered at all rather than shown
   *  permanently empty. */
  available?: boolean;
}

const IncidentsLegend = ({
  counts,
  total,
  days,
}: {
  counts: Partial<Record<IncidentStatus, number>>;
  total: number;
  days: number;
}) => {
  const slot = useLegendSlot("incidents", ALL_LEGEND_SLOTS, LEGEND_PRIORITY.incidents);
  const rows = (Object.keys(INCIDENT_STATUS_META) as IncidentStatus[]).filter(
    (s) => (counts[s] ?? 0) > 0
  );
  return (
    <Box
      sx={{
        position: "absolute",
        ...LEGEND_SLOT_STYLES[slot],
        width: LEGEND_WIDTH,
        backgroundColor: "rgba(30, 41, 59, 0.95)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(148, 163, 184, 0.25)",
        borderRadius: "10px",
        padding: "10px 12px",
        zIndex: 1000,
        color: "#f8fafc",
        fontSize: "12px",
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
        Incidents
      </Typography>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8" }}>
          None on record{days ? ` in the last ${days} days` : ""}.
        </Typography>
      ) : (
        rows.map((status) => (
          <Box
            key={status}
            sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.35 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: INCIDENT_STATUS_META[status].color,
                opacity: INCIDENT_STATUS_META[status].closed ? 0.6 : 1,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: "0.62rem", color: "#cbd5e1", flexGrow: 1 }}>
              {INCIDENT_STATUS_META[status].label}
            </Typography>
            <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8" }}>
              {counts[status]}
            </Typography>
          </Box>
        ))
      )}
      <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8", mt: 0.5 }}>
        {total} shown · dashed = closed · dotted line = rekindle link · click a
        number for details
      </Typography>
    </Box>
  );
};

const IncidentsLayer = ({
  id,
  name,
  projectId,
  days,
  groups,
  selectedIncidentId,
  onSelect,
  focusSignal,
  onOpenDetail,
  available = true,
}: IncidentsLayerProps) => {
  const { map } = useMap();
  // Off until asked for, like the other realtime layers: a project map opens
  // on the landscape, and live incidents are something you switch on to look
  // at. Focusing an incident from its list still reveals the layer.
  const [shown, setShown] = useState(false);
  // Derived rather than forced off in an effect, so an unavailable layer is
  // never briefly drawn on the first render.
  const active = shown && available;
  const [stale, setStale] = useState(true);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const [popoverIncidentId, setPopoverIncidentId] = useState<number | null>(null);
  const [closing, setClosing] = useState<IncidentSummary | null>(null);

  // Nothing to poll for every 60 s when the layer is not on offer.
  const queryProjectId = available ? projectId : undefined;
  const { data } = useProjectIncidents(queryProjectId, { days });
  const { data: agentData } = useProjectAgentSessions(queryProjectId);
  const agentSessions = agentData?.data?.sessions ?? {};
  const agentEnabled = agentData?.data?.enabled ?? false;

  const allIncidents = useMemo(() => data?.data?.incidents ?? [], [data]);
  const incidents = useMemo(() => {
    const statuses = visibleStatuses(groups);
    // Oldest first. Mapbox draws a layer's features in source order, so the
    // last one wins where they overlap, and a click reports the topmost
    // feature first. A fire that comes back opens a new incident on top of
    // the old one's footprint, so the newest incident must be the one drawn
    // (and clicked) on top. The API lists newest-activity first, which drew
    // the OLD incident over the new one and made the new one unclickable.
    return allIncidents
      .filter((i) => statuses.has(i.status))
      .sort((a, b) => a.id - b.id);
  }, [allIncidents, groups]);
  const byId = useMemo(
    () => new Map(allIncidents.map((i) => [i.id, i])),
    [allIncidents]
  );

  // The selected incident and its whole lineage are emphasised together.
  const selectedIds = useMemo(() => {
    const ids = new Set<number>();
    if (selectedIncidentId === null) return ids;
    ids.add(selectedIncidentId);
    const incident = byId.get(selectedIncidentId);
    incident?.chain.forEach((c) => ids.add(c));
    incident?.rekindles.forEach((r) => ids.add(r));
    return ids;
  }, [selectedIncidentId, byId]);

  const geojson = useMemo<FeatureCollection>(() => {
    if (!active || !projectId) return EMPTY_FC;
    // Hulls first, links over them, markers on top.
    return {
      type: "FeatureCollection",
      features: [
        ...incidentHullFeatures(incidents, selectedIds),
        ...incidentLinkFeatures(incidents, selectedIds),
        ...incidentMarkerFeatures(incidents, selectedIds),
      ],
    };
  }, [active, projectId, incidents, selectedIds]);

  // Data refreshes go straight into the live source (realtime-layer
  // precedent) so the 60 s poll never flickers the layer.
  useEffect(() => {
    if (!map) return;
    // getSource reaches into map.style, which is gone once the map has been
    // removed (route change mid-poll); Mapbox then throws "getOwnSource of
    // undefined" and the ErrorBoundary unmounted the project page
    // (fisci-front-end #408).
    try {
      const source = map.getSource(id) as mapboxgl.GeoJSONSource | undefined;
      if (!source || typeof source.setData !== "function") return;
      source.setData(geojson);
    } catch (e) {
      console.debug("incidents setData skipped:", e);
    }
  }, [geojson, map, id]);

  // "Zoom to": the selected incident plus its predecessors and rekindles.
  // A deep link fires the signal before the map or the incidents exist, so
  // the request is held until both have arrived, then consumed once.
  const pendingFocusRef = useRef(0);
  useEffect(() => {
    if (!focusSignal) return;
    pendingFocusRef.current = focusSignal;
    // Asking to see an incident is asking for the layer: with it off by
    // default, zooming to an empty spot would be the deep link's only result.
    setShown(true);
  }, [focusSignal]);
  useEffect(() => {
    if (!map || !pendingFocusRef.current || selectedIncidentId === null) return;
    const incident = byId.get(selectedIncidentId);
    if (!incident) return;
    const bounds = incidentLineageBounds(incident, allIncidents);
    if (bounds) map.fitBounds(bounds, { padding: 120, maxZoom: 13, duration: 800 });
    pendingFocusRef.current = 0;
  }, [focusSignal, map, byId, allIncidents, selectedIncidentId]);

  const layerStyle = useMemo(
    () => [
      incidentHullFillStyle(id),
      incidentHullLineStyle(id),
      incidentLinkStyle(id),
      incidentMarkerStyle(id),
      incidentMarkerLabelStyle(id),
    ],
    [id]
  );
  // Markers and hull fills take clicks; the label symbol rides on the marker.
  const interactiveLayerIds = useMemo(
    () => [`${id}-markers`, `${id}-hull-fill`],
    [id]
  );

  type IncidentFeatureHit = { properties?: { incident_id?: number; feature_kind?: string } };
  const handleClick = (e: mapboxgl.MapMouseEvent & { features?: IncidentFeatureHit[] }) => {
    if (!e.features || e.features.length === 0) return;
    const features = e.features;
    // A marker over a hull wins; a hull alone opens its own incident.
    const marker = features.find((f) => f.properties?.feature_kind === "marker");
    const hit = marker ?? features[0];
    const incidentId = hit.properties?.incident_id;
    if (incidentId === undefined) return;
    onSelect(Number(incidentId));
    setPopoverIncidentId(Number(incidentId));
    setPopoverPosition(mapEventScreenPoint(e));
  };

  const handleClosePopover = () => {
    setPopoverIncidentId(null);
    setPopoverPosition(null);
  };

  const toolbarButton = (
    <ToolbarButton
      key={id}
      id={id}
      name={name}
      toggledOn={active}
      enabled={!!projectId && available}
      onClick={() => setShown((prev) => !prev)}
      icon={"report"}
    />
  );

  const popoverIncident =
    popoverIncidentId !== null ? byId.get(popoverIncidentId) ?? null : null;

  const popover = projectId ? (
    <>
      <IncidentPopover
        projectId={projectId}
        incident={popoverIncident}
        agentSession={
          popoverIncident ? agentSessions[String(popoverIncident.id)] : undefined
        }
        agentEnabled={agentEnabled}
        anchorPosition={popoverPosition}
        onClose={handleClosePopover}
        onZoom={() => {
          if (popoverIncident) {
            const bounds = incidentLineageBounds(popoverIncident, allIncidents);
            if (bounds && map)
              map.fitBounds(bounds, { padding: 120, maxZoom: 13, duration: 800 });
          }
        }}
        onSelectIncident={(nextId) => {
          // Switch the popover to the related incident and centre on it.
          onSelect(nextId);
          setPopoverIncidentId(nextId);
          const next = byId.get(nextId);
          if (next?.centroid && map) {
            map.easeTo({ center: next.centroid, duration: 600 });
          }
        }}
        onOpenDetail={() => {
          if (popoverIncident) {
            handleClosePopover();
            onOpenDetail(popoverIncident.id);
          }
        }}
        onRequestClose={() => setClosing(popoverIncident)}
      />
      <CloseIncidentDialog
        projectId={projectId}
        incident={closing}
        hasAgent={
          closing !== null && agentSessions[String(closing.id)]?.status === "active"
        }
        onClose={() => setClosing(null)}
        onClosed={handleClosePopover}
      />
    </>
  ) : null;

  const counts = useMemo(() => {
    const out: Partial<Record<IncidentStatus, number>> = {};
    for (const incident of incidents) {
      out[incident.status] = (out[incident.status] ?? 0) + 1;
    }
    return out;
  }, [incidents]);

  const customComponent =
    active && projectId ? (
      <IncidentsLegend counts={counts} total={incidents.length} days={days} />
    ) : null;

  return {
    id,
    name,
    source: geojson,
    rawSource: geojson,
    layerStyle,
    toolbarButton,
    active,
    stale,
    setStale,
    interactiveLayerIds,
    onClick: handleClick,
    popover,
    customComponent,
    // Exposed for the control panel: what the layer currently shows.
    incidentCount: incidents.length,
  };
};

export default IncidentsLayer;
